import { getAllFactoringReSelling } from 'lib/api/reSell/factoring';
import {
  CompanyDocumentResell,
  GetAllCompanyDocumentResellsResponse,
} from 'lib/api/reSell/factoring/types';
import { AutocompleteOption } from 'lib/common/GlobalSearcher/filterTypes';
import { PaginationInfo } from 'pages/MyAccounts/types/businessRelations';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

export interface ResellWithSuggestions extends CompanyDocumentResell {
  suggestedAmountPayment: number;
  suggestedAmountBuyback: number;
}

export interface FetchResellingsParams {
  page: number;
  pageSize: number;
  exportComplete?: boolean;
}

export interface SearchFilters {
  businessIdentifier: string;
  stakeholderIdentifier: string;
  folio: string;
  fundId: string | null;
  selectedFund: AutocompleteOption | null;
  status: string;
  startCreatedAt: string | null;
  endCreatedAt: string | null;
  startDueDate: string | null;
  endDueDate: string | null;
  selectedSortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface FactoringResellContextValue {
  resells: CompanyDocumentResell[];
  resellsSelected: ResellWithSuggestions[];
  resell: CompanyDocumentResell | null;
  originalResell: CompanyDocumentResell | null;
  pagination: PaginationInfo;
  selectedResellIds: number[];
  loading: boolean;
  edit: boolean;

  setResells: React.Dispatch<React.SetStateAction<CompanyDocumentResell[]>>;
  setResellsSelected: React.Dispatch<
    React.SetStateAction<ResellWithSuggestions[]>
  >;
  setResell: React.Dispatch<React.SetStateAction<CompanyDocumentResell | null>>;
  setOriginalResell: React.Dispatch<
    React.SetStateAction<CompanyDocumentResell | null>
  >;
  setPagination: React.Dispatch<React.SetStateAction<PaginationInfo>>;
  setSelectedResellIds: React.Dispatch<React.SetStateAction<number[]>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;

  // Estados de UI (dialogos)
  openNewResell: boolean;
  setOpenNewResell: React.Dispatch<React.SetStateAction<boolean>>;
  openMultiSales: boolean;
  setOpenMultiSales: React.Dispatch<React.SetStateAction<boolean>>;
  openMultiPayment: boolean;
  setOpenMultiPayment: React.Dispatch<React.SetStateAction<boolean>>;
  openMultiBuyBack: boolean;
  setOpenMultiBuyBack: React.Dispatch<React.SetStateAction<boolean>>;
  openRegisterSelector: boolean;
  setOpenRegisterSelector: React.Dispatch<React.SetStateAction<boolean>>;
  openRevert: boolean;
  setOpenRevert: React.Dispatch<React.SetStateAction<boolean>>;
  resellToRevert: CompanyDocumentResell | null;
  setResellToRevert: React.Dispatch<
    React.SetStateAction<CompanyDocumentResell | null>
  >;

  // Filtros de busqueda
  filters: SearchFilters;
  setBusinessIdentifier: React.Dispatch<React.SetStateAction<string>>;
  setStakeholderIdentifier: React.Dispatch<React.SetStateAction<string>>;
  setFolio: React.Dispatch<React.SetStateAction<string>>;
  setFundId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedFund: React.Dispatch<
    React.SetStateAction<AutocompleteOption | null>
  >;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  setStartCreatedAt: React.Dispatch<React.SetStateAction<string | null>>;
  setEndCreatedAt: React.Dispatch<React.SetStateAction<string | null>>;
  setStartDueDate: React.Dispatch<React.SetStateAction<string | null>>;
  setEndDueDate: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedSortBy: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;

  // Acciones
  fetchResellings: (
    params: FetchResellingsParams,
  ) => Promise<GetAllCompanyDocumentResellsResponse | undefined>;
  triggerSearch: () => Promise<void>;
  handleDetails: (id: string) => void;

  // Constantes
  PAGE_SIZE: number;
}

const PAGE_SIZE = 10;

// Contexto con valor por defecto
export const FactoringResellContext =
  createContext<FactoringResellContextValue | null>(null);

// Props del Provider
interface FactoringResellProviderProps {
  children: ReactNode;
}

// Provider Component
export const FactoringResellProvider: React.FC<
  FactoringResellProviderProps
> = ({ children }) => {
  const dispatch = useDispatch();

  // Datos principales
  const [resells, setResells] = useState<CompanyDocumentResell[]>([]);
  const [resellsSelected, setResellsSelected] = useState<
    ResellWithSuggestions[]
  >([]);
  const [resell, setResell] = useState<CompanyDocumentResell | null>(null);
  const [originalResell, setOriginalResell] =
    useState<CompanyDocumentResell | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [selectedResellIds, setSelectedResellIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);

  // Estados de UI (dialogos)
  const [openNewResell, setOpenNewResell] = useState(false);
  const [openMultiSales, setOpenMultiSales] = useState(false);
  const [openMultiPayment, setOpenMultiPayment] = useState(false);
  const [openMultiBuyBack, setOpenMultiBuyBack] = useState(false);
  const [openRegisterSelector, setOpenRegisterSelector] = useState(false);
  const [openRevert, setOpenRevert] = useState(false);
  const [resellToRevert, setResellToRevert] =
    useState<CompanyDocumentResell | null>(null);

  // Filtros de busqueda
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [folio, setFolio] = useState('');
  const [fundId, setFundId] = useState<string | null>(null);
  const [selectedFund, setSelectedFund] = useState<AutocompleteOption | null>(
    null,
  );
  const [status, setStatus] = useState('ACTIVE');
  const [startCreatedAt, setStartCreatedAt] = useState<string | null>(null);
  const [endCreatedAt, setEndCreatedAt] = useState<string | null>(null);
  const [startDueDate, setStartDueDate] = useState<string | null>(null);
  const [endDueDate, setEndDueDate] = useState<string | null>(null);
  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Objeto de filtros para exponer
  const filters: SearchFilters = {
    businessIdentifier,
    stakeholderIdentifier,
    folio,
    fundId,
    selectedFund,
    status,
    startCreatedAt,
    endCreatedAt,
    startDueDate,
    endDueDate,
    selectedSortBy,
    sortOrder,
  };

  // Generador de query string
  const generateQuery = useCallback(
    ({
      page,
      pageSize,
      exportComplete,
    }: {
      page: number;
      pageSize: number;
      exportComplete?: boolean;
    }) => {
      let query = '';
      if (status && status !== 'ALL') query += `status=${status}&`;
      if (businessIdentifier)
        query += `businessIdentifier=${businessIdentifier}&`;
      if (stakeholderIdentifier)
        query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
      if (folio) query += `folio=${folio}&`;
      if (fundId) query += `fundId=${fundId}&`;
      if (startCreatedAt) query += `startCreatedAt=${startCreatedAt}&`;
      if (endCreatedAt) query += `endCreatedAt=${endCreatedAt}&`;
      if (startDueDate) query += `startDueDate=${startDueDate}&`;
      if (endDueDate) query += `endDueDate=${endDueDate}&`;
      if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
      if (sortOrder) query += `sortOrder=${sortOrder}&`;
      if (page) query += `page=${page}&pageSize=${pageSize}&`;
      if (exportComplete) query += `all=true&`;
      return query;
    },
    [
      status,
      businessIdentifier,
      stakeholderIdentifier,
      folio,
      fundId,
      startCreatedAt,
      endCreatedAt,
      startDueDate,
      endDueDate,
      selectedSortBy,
      sortOrder,
    ],
  );

  // Fetch de datos
  const fetchResellings = useCallback(
    async ({ page, pageSize, exportComplete }: FetchResellingsParams) => {
      setLoading(true);
      try {
        const query = generateQuery({ page, pageSize, exportComplete });
        const response = await getAllFactoringReSelling(dispatch, query);
        if (response && !exportComplete) {
          setResells(response.data);
          setPagination(response.pagination);
        }
        setLoading(false);
        return response;
      } catch (error) {
        alert('Error al cargar los pagos');
        setLoading(false);
      }
    },
    [dispatch, generateQuery],
  );

  // Trigger de busqueda
  const triggerSearch = useCallback(async () => {
    setOpenNewResell(false);
    await fetchResellings({ page: 0, pageSize: PAGE_SIZE });
  }, [fetchResellings]);

  // Mostrar detalles de un resell
  const handleDetails = useCallback(
    (id: string) => {
      const resellSearched = resells.find((r) => r.id === Number(id));
      if (!resellSearched) return;
      setResell(resellSearched);
      setOriginalResell({ ...resellSearched });
      setOpenNewResell(true);
      setEdit(true);
    },
    [resells],
  );

  const value: FactoringResellContextValue = {
    // Datos
    resells,
    resellsSelected,
    resell,
    originalResell,
    pagination,
    selectedResellIds,
    loading,
    edit,

    // Setters de datos
    setResells,
    setResellsSelected,
    setResell,
    setOriginalResell,
    setPagination,
    setSelectedResellIds,
    setLoading,
    setEdit,

    // Estados de UI
    openNewResell,
    setOpenNewResell,
    openMultiSales,
    setOpenMultiSales,
    openMultiPayment,
    setOpenMultiPayment,
    openMultiBuyBack,
    setOpenMultiBuyBack,
    openRegisterSelector,
    setOpenRegisterSelector,
    openRevert,
    setOpenRevert,
    resellToRevert,
    setResellToRevert,

    // Filtros
    filters,
    setBusinessIdentifier,
    setStakeholderIdentifier,
    setFolio,
    setFundId,
    setSelectedFund,
    setStatus,
    setStartCreatedAt,
    setEndCreatedAt,
    setStartDueDate,
    setEndDueDate,
    setSelectedSortBy,
    setSortOrder,

    // Acciones
    fetchResellings,
    triggerSearch,
    handleDetails,

    // Constantes
    PAGE_SIZE,
  };

  return (
    <FactoringResellContext.Provider value={value}>
      {children}
    </FactoringResellContext.Provider>
  );
};

export const useFactoringResell = (): FactoringResellContextValue => {
  const context = useContext(FactoringResellContext);
  if (!context) {
    throw new Error(
      'useFactoringResell debe usarse dentro de un FactoringResellProvider',
    );
  }
  return context;
};
